import useDateFormatters from "@hooks/useDateFormatters"

import {
    DownPaymentBadge,
    IssueDateBadge,
    LabelsBadges,
    LastModifiedBadge,
    TermsBadge,
    TotalBadge,
} from "@molecules/ObjectCard/Attributes/AttributeBadges"
import ObjectCard from "@molecules/ObjectCard/ObjectCard"
import { composeClientLocation, shouldShowAttribute } from "@molecules/ObjectCard/ObjectCards/ObjectCards.utils"
import ObjectStatusBadge from "@molecules/ObjectStatusBadge/ObjectStatusBadge"

import { EstimateCardProps } from "./EstimateCard.types"

type EstimateTimestampKey =
    | "created_at"
    | "last_sent_at"
    | "last_approved_at"
    | "last_won_at"
    | "last_lost_at"
    | "last_cancelled_at"
    | "last_status_transition_time"

const STATUS_TRANSITION: Record<Estimate["status_label"], { verb: string; timestampKey: EstimateTimestampKey }> = {
    Draft: { verb: "Created", timestampKey: "created_at" },
    Unsent: { verb: "Created", timestampKey: "created_at" },
    "Awaiting Approval": { verb: "Sent", timestampKey: "last_sent_at" },
    Expired: { verb: "Expired", timestampKey: "last_status_transition_time" },
    Unconverted: { verb: "Approved", timestampKey: "last_approved_at" },
    Jobbed: { verb: "Won", timestampKey: "last_won_at" },
    Invoiced: { verb: "Won", timestampKey: "last_won_at" },
    Lost: { verb: "Lost", timestampKey: "last_lost_at" },
    Cancelled: { verb: "Cancelled", timestampKey: "last_cancelled_at" },
}

// Terms only expire while the estimate awaits a decision; `is_expired` is date-based alone, so an
// approved, lost, or cancelled estimate past its due date would otherwise show the expired look.
const AWAITING_DECISION_STATUS_LABELS: Estimate["status_label"][] = ["Draft", "Unsent", "Awaiting Approval", "Expired"]

export default function EstimateCard(props: EstimateCardProps) {
    const { estimate, displayedAttributes, onClick, isLoading = false, isTabbable, className, ...errorProps } = props

    const { formatDatetimeTooltip } = useDateFormatters()

    const { verb, timestampKey } = STATUS_TRANSITION[estimate.status_label]
    const statusTooltip = formatDatetimeTooltip(
        verb,
        estimate[timestampKey] ?? estimate.last_status_transition_time ?? estimate.created_at,
    )

    const showTotal = shouldShowAttribute(displayedAttributes, "total") && estimate.total != null
    const showIssued = shouldShowAttribute(displayedAttributes, "date_issued") && Boolean(estimate.date_issued)
    const showTerms =
        shouldShowAttribute(displayedAttributes, "terms") &&
        Boolean(estimate.date_issued) &&
        Boolean(estimate.date_due)
    const showDownPayment =
        shouldShowAttribute(displayedAttributes, "down_payment_status") &&
        estimate.down_payment_status !== "Not Required"
    const showLastModified =
        shouldShowAttribute(displayedAttributes, "last_modified_at") && Boolean(estimate.last_modified_at)
    const showLabels = shouldShowAttribute(displayedAttributes, "labels") && estimate.labels.length > 0

    const hasAttributes = showTotal || showIssued || showTerms || showDownPayment || showLastModified || showLabels

    return (
        <ObjectCard
            onClick={onClick}
            isLoading={isLoading}
            isTabbable={isTabbable}
            className={className}
            {...errorProps}
        >
            <ObjectCard.Header
                id={estimate.custom_id ?? String(estimate.id)}
                showSeenIcon={Boolean(estimate.last_viewed)}
                statusBadge={
                    <ObjectStatusBadge
                        objectType="estimate"
                        status={estimate.status_label}
                        size="md"
                        tooltip={statusTooltip}
                    />
                }
            />
            <ObjectCard.Body primaryText={estimate.service_name} secondaryText={composeClientLocation(estimate)} />
            {(isLoading || hasAttributes) && (
                <ObjectCard.Attributes>
                    {showTotal && <TotalBadge value={estimate.total} />}
                    {showIssued && <IssueDateBadge date={estimate.date_issued} />}
                    {showTerms && (
                        <TermsBadge
                            variant="dates"
                            dateIssued={estimate.date_issued}
                            dateDue={estimate.date_due}
                            isExpired={
                                estimate.is_expired && AWAITING_DECISION_STATUS_LABELS.includes(estimate.status_label)
                            }
                        />
                    )}
                    {showDownPayment && (
                        <DownPaymentBadge
                            status={estimate.down_payment_status}
                            amount={estimate.down_payment_amount}
                            amountDue={estimate.down_payment_amount_due}
                            isOverdue={estimate.is_expired}
                        />
                    )}
                    {showLastModified && <LastModifiedBadge date={estimate.last_modified_at} />}
                    {showLabels && <LabelsBadges labels={estimate.labels} />}
                </ObjectCard.Attributes>
            )}
        </ObjectCard>
    )
}
